import { handleAPIResponse, getAuthHeaders } from '@/utils/apiUtils';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

export interface Integration {
  id: string;
  name: string;
  version: string;
  providerKey: string;
  isActive: boolean;
  config?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationConnection {
  id: string;
  organisationId: string;
  integrationId: string;
  status: 'connected' | 'disconnected' | 'error' | 'expired';
  scopes?: string[] | null;
  tokenExpiresAt: string;
  accountsServer?: string | null;
  apiDomain?: string | null;
  location?: string | null;
  connectedAt: string;
  connectedBy?: string | null;
  lastVerifiedAt?: string | null;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
  integration?: Integration;
  connectedByEmployee?: {
    id: string;
    name: string;
    email: string;
  };
}

// Unused but kept for potential future use
// interface IntegrationsResponse {
//   success: boolean;
//   data: Integration[];
//   message: string;
// }

// interface ConnectionsResponse {
//   success: boolean;
//   data: IntegrationConnection[];
//   message: string;
// }

interface ConnectionResponse {
  success: boolean;
  data: IntegrationConnection;
  message: string;
}

interface ConnectZohoResponse {
  success: boolean;
  data: {
    authUrl: string;
    state: string;
  };
  message: string;
}

interface VerifyConnectionResponse {
  success: boolean;
  data: {
    connection: IntegrationConnection;
    isValid: boolean;
  };
  message: string;
}

export const integrationsApi = {
  // Get all available integrations
  getIntegrations: async (): Promise<Integration[]> => {
    const response = await fetch(`${API_BASE_URL}/integrations`, {
      headers: getAuthHeaders(),
    });

    // handleAPIResponse already extracts result.data, so we return it directly
    const data = await handleAPIResponse<Integration[]>(response);
    // Ensure we return an array even if the API returns null/undefined
    return Array.isArray(data) ? data : [];
  },

  // Get all connections for current organization
  getConnections: async (): Promise<IntegrationConnection[]> => {
    const response = await fetch(`${API_BASE_URL}/integrations/connections`, {
      headers: getAuthHeaders(),
    });

    // handleAPIResponse already extracts result.data, so we return it directly
    const data = await handleAPIResponse<IntegrationConnection[]>(response);
    // Ensure we return an array even if the API returns null/undefined
    return Array.isArray(data) ? data : [];
  },

  // Initiate Zoho OAuth flow
  connectZoho: async (): Promise<string> => {
    const response = await fetch(`${API_BASE_URL}/integrations/zoho/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    const result = await handleAPIResponse<
      ConnectZohoResponse | (ConnectZohoResponse['data'] & { success?: boolean; message?: string })
    >(response);

    // Support both wrapped (`{ data: { authUrl } }`) and flat (`{ authUrl }`) responses
    const resultWithData = result as ConnectZohoResponse | (ConnectZohoResponse['data'] & { success?: boolean; message?: string });
    const authUrl =
      ('data' in resultWithData && resultWithData.data?.authUrl) ||
      ('authUrl' in resultWithData ? resultWithData.authUrl : undefined);

    if (!authUrl || typeof authUrl !== 'string') {
      throw new Error('Missing Zoho authorization URL from server response.');
    }

    return authUrl;
  },

  // Get connection status
  getConnectionStatus: async (connectionId: string): Promise<IntegrationConnection> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${connectionId}/status`, {
      headers: getAuthHeaders(),
    });

    const result = await handleAPIResponse<ConnectionResponse>(response);
    return result.data;
  },

  // Verify connection
  verifyConnection: async (connectionId: string): Promise<VerifyConnectionResponse['data']> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${connectionId}/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    const result = await handleAPIResponse<VerifyConnectionResponse>(response);
    return result.data;
  },

  getZohoJobOpenings: async (
    connectionId: string,
    lookbackDays?: number
  ): Promise<{
    jobs: Array<{
      externalId: string;
      zohoApiRecordId: string;
      title: string;
      createdAt?: string | null;
      modifiedAt?: string | null;
    }>;
    lookbackDays: number;
    fromCache: boolean;
  }> => {
    const qs =
      lookbackDays !== undefined ? `?lookbackDays=${encodeURIComponent(String(lookbackDays))}` : '';
    const response = await fetch(
      `${API_BASE_URL}/integrations/zoho/connections/${connectionId}/job-openings${qs}`,
      { headers: getAuthHeaders({ addContentType: false }) }
    );
    return handleAPIResponse(response);
  },

  getIntegrationJobs: async (
    connectionId: string
  ): Promise<{
    jobs: Array<{
      id: string;
      externalId: string;
      zohoApiRecordId: string | null;
      title: string;
      syncEnabled: boolean;
      updatedAt?: string;
    }>;
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/integrations/zoho/connections/${connectionId}/integration-jobs`,
      { headers: getAuthHeaders({ addContentType: false }) }
    );
    return handleAPIResponse(response);
  },

  putIntegrationJobs: async (
    connectionId: string,
    body: {
      jobs: Array<{
        externalId: string;
        zohoApiRecordId?: string | null;
        title: string;
        syncEnabled: boolean;
      }>;
    }
  ): Promise<{
    jobs: Array<{
      id: string;
      externalId: string;
      zohoApiRecordId: string | null;
      title: string;
      syncEnabled: boolean;
      updatedAt?: string;
    }>;
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/integrations/zoho/connections/${connectionId}/integration-jobs`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );
    return handleAPIResponse(response);
  },

  patchIntegrationJob: async (
    connectionId: string,
    integrationJobId: string,
    body: { syncEnabled: boolean }
  ): Promise<{
    id: string;
    externalId: string;
    zohoApiRecordId: string | null;
    title: string;
    syncEnabled: boolean;
    updatedAt?: string;
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/integrations/zoho/connections/${connectionId}/integration-jobs/${integrationJobId}`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );
    return handleAPIResponse(response);
  },

  // Disconnect integration
  disconnectIntegration: async (connectionId: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${connectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleAPIResponse(response);
  },

  // Slack integration APIs
  getSlackStatus: async (organisationId: string): Promise<{
    connected: boolean;
    slackTeamName?: string;
    mappedCount?: number;
    totalCount?: number;
  }> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/slack`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  getSlackUsers: async (organisationId: string): Promise<Array<{
    employeeId: string;
    employeeName: string;
    employeeEmail: string;
    slackUserId?: string;
    slackUserName?: string;
    mapped: boolean;
  }>> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/slack/users`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  resyncSlackUsers: async (): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/integrations/slack/resync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    await handleAPIResponse(response);
  },

  // Initiate Slack OAuth flow
  connectSlack: async (): Promise<string> => {
    const response = await fetch(`${API_BASE_URL}/integrations/slack/install`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    // Handle redirect response
    if (response.redirected && response.url) {
      return response.url;
    }

    // Handle JSON response with URL
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      interface SlackAuthResponse {
        url?: string;
        authUrl?: string;
        data?: {
          url?: string;
          authUrl?: string;
        };
      }

      const result = await handleAPIResponse<SlackAuthResponse | string>(response);

      if (typeof result === 'string') {
        return result;
      }

      const authResponse = result as SlackAuthResponse;
      const authUrl =
        authResponse?.url ||
        authResponse?.authUrl ||
        authResponse?.data?.url ||
        authResponse?.data?.authUrl;

      if (authUrl && typeof authUrl === 'string') {
        return authUrl;
      }
    }

    // If response is a redirect, get location header
    if (response.status >= 300 && response.status < 400) {
      const location = response.headers.get('location');
      if (location) {
        return location;
      }
    }

    throw new Error('Missing Slack authorization URL from server response.');
  },

  // Get available Slack users for matching (unmapped Slack users)
  getAvailableSlackUsers: async (organisationId: string): Promise<Array<{
    slackUserId: string;
    slackUserName: string;
    slackUserEmail?: string;
    slackUserDisplayName?: string;
  }>> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/slack/users/available`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  // Map an employee to a Slack user
  mapEmployeeToSlackUser: async (
    organisationId: string,
    employeeId: string,
    slackUserId: string
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/organisations/${organisationId}/slack/map`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId,
          slackUserId,
        }),
      }
    );

    await handleAPIResponse(response);
  },

  // Unmap an employee from Slack user
  unmapEmployeeFromSlackUser: async (
    organisationId: string,
    employeeId: string
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/organisations/${organisationId}/slack/unmap`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId,
        }),
      }
    );

    await handleAPIResponse(response);
  },

  // Disconnect Slack integration
  disconnectSlack: async (): Promise<{ deletedMappings: number }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/slack`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const result = await handleAPIResponse<{ deletedMappings: number }>(response);
    return result;
  },

  // Microsoft Teams integration APIs
  getTeamsStatus: async (organisationId: string): Promise<{
    connected: boolean;
    tenantId?: string;
    mappedCount?: number;
    totalCount?: number;
  }> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/teams`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  getTeamsUsers: async (organisationId: string): Promise<Array<{
    employeeId: string;
    employeeName: string;
    employeeEmail: string;
    teamsUserId?: string;
    teamsUserName?: string;
    mapped: boolean;
  }>> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/teams/users`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  resyncTeamsUsers: async (): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/integrations/teams/resync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    await handleAPIResponse(response);
  },

  // Initiate Teams OAuth flow
  connectTeams: async (): Promise<string> => {
    const response = await fetch(`${API_BASE_URL}/integrations/teams/install`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    // Handle redirect response
    if (response.redirected && response.url) {
      return response.url;
    }

    // Handle JSON response with URL
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      interface TeamsAuthResponse {
        url?: string;
        authUrl?: string;
        data?: {
          url?: string;
          authUrl?: string;
        };
      }

      const result = await handleAPIResponse<TeamsAuthResponse | string>(response);

      if (typeof result === 'string') {
        return result;
      }

      const authResponse = result as TeamsAuthResponse;
      const authUrl =
        authResponse?.url ||
        authResponse?.authUrl ||
        authResponse?.data?.url ||
        authResponse?.data?.authUrl;

      if (authUrl && typeof authUrl === 'string') {
        return authUrl;
      }
    }

    // If response is a redirect, get location header
    if (response.status >= 300 && response.status < 400) {
      const location = response.headers.get('location');
      if (location) {
        return location;
      }
    }

    throw new Error('Missing Teams authorization URL from server response.');
  },

  // Get available Teams users for matching (unmapped Teams users)
  getAvailableTeamsUsers: async (organisationId: string): Promise<Array<{
    teamsUserId: string;
    teamsUserName: string;
    teamsUserEmail?: string;
    teamsUserDisplayName?: string;
  }>> => {
    const response = await fetch(`${API_BASE_URL}/organisations/${organisationId}/teams/users/available`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse(response);
  },

  // Map an employee to a Teams user
  mapEmployeeToTeamsUser: async (
    organisationId: string,
    employeeId: string,
    teamsUserId: string
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/organisations/${organisationId}/teams/map`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId,
          teamsUserId,
        }),
      }
    );

    await handleAPIResponse(response);
  },

  // Unmap an employee from Teams user
  unmapEmployeeFromTeamsUser: async (
    organisationId: string,
    employeeId: string
  ): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/organisations/${organisationId}/teams/unmap`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId,
        }),
      }
    );

    await handleAPIResponse(response);
  },

  // Disconnect Teams integration
  disconnectTeams: async (): Promise<{ deletedMappings: number }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/teams`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const result = await handleAPIResponse<{ deletedMappings: number }>(response);
    return result;
  },
};

